import { useEffect, useMemo } from "react"

import { formatNumber } from "accounting"
import { Col, Flex, Row, Spin, Typography } from "antd"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"

import Button from "../../../Components/Button"
import EmptyComponent from "../../../Components/Empty"
import InfoTooltip from "../../../Components/InfoTooltip/InfoTooltip"
import Private from "../../../Components/Private"
import Table from "../../../Components/Table"
import { RoutesPaths } from "../../../constants/user-routes"
import Icon from "../../../Icon"
import { formatCurrency } from "../../../Utils/utils"
import AnalyticsFilters from "../components/AnalyticsFilters"
import { sortableTitle } from "../components/sortableTitle"
import SummaryRows from "../components/SummaryRows/SummaryRows"
import WinRateGrid from "../components/WinRateGrid/WinRateGrid"
import { useAnalyticsScope } from "../hooks/useAnalyticsScope"
import { ANALYTICS_SECTION } from "../redux/analytics.constants"
import { selectSectionData, selectSectionFetching } from "../redux/analytics.slice"
import { toWinRateGrid } from "../utils/breakdowns"

import styles from "../analytics.module.less"
import tables from "../tables.module.less"

const { Title } = Typography

// Inner full page for the win-rate matrix, mirroring the Top countries / Activity
// stream sub-pages: back link, title, the shared calendar, the full heat map (every
// category, not just the card's top 7) and a table with the exact figures behind each
// row. The heat map is the card's own answer; the table is by-category/, the donut's.

const pct = (v) => (v == null ? "-" : `${Math.round(v)}%`)
const num = (v) => formatNumber(v, 0, ",")

const columns = (t) => [
  {
    title: sortableTitle(t("Category"), "label"),
    dataIndex: "label",
    key: "label",
    sorter: (a, b) => a.label.localeCompare(b.label)
  },
  {
    title: sortableTitle(t("Win rate"), "winRate", { align: "right" }),
    dataIndex: "winRate",
    key: "winRate",
    width: 140,
    align: "right",
    sorter: (a, b) => (a.winRate ?? -1) - (b.winRate ?? -1),
    render: pct
  },
  {
    title: sortableTitle(t("Won"), "won", { align: "right" }),
    dataIndex: "won",
    key: "won",
    width: 110,
    align: "right",
    sorter: (a, b) => a.won - b.won,
    render: num
  },
  {
    title: sortableTitle(t("Resolved"), "resolved", { align: "right" }),
    dataIndex: "resolved",
    key: "resolved",
    width: 120,
    align: "right",
    sorter: (a, b) => a.resolved - b.resolved,
    render: num
  },
  {
    title: sortableTitle(t("Disputes"), "count", { align: "right" }),
    dataIndex: "count",
    key: "count",
    width: 120,
    align: "right",
    defaultSortOrder: "descend",
    sorter: (a, b) => a.count - b.count,
    render: num
  },
  {
    title: sortableTitle(t("Volume ($)"), "value", { align: "right" }),
    dataIndex: "value",
    key: "value",
    width: 160,
    align: "right",
    sorter: (a, b) => a.value - b.value,
    render: (v) => <Private>{formatCurrency(v, false)}</Private>
  }
]

// The summary's cells under the columns after Category.
const SUMMARY_CELLS = [
  { align: "right", render: (s) => pct(s.win_rate) },
  { align: "right", render: (s) => num(s.won_count) },
  { align: "right", render: (s) => num(s.resolved_count) },
  { align: "right", render: (s) => num(s.count) },
  { align: "right", render: (s) => <Private>{formatCurrency(s.amount, false)}</Private> }
]

const WinRateFullPage = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { getBase, getCompare } = useAnalyticsScope()
  const matrix = useSelector(selectSectionData(ANALYTICS_SECTION.WIN_RATE_MATRIX))
  const matrixFetching = useSelector(selectSectionFetching(ANALYTICS_SECTION.WIN_RATE_MATRIX))
  const byCategory = useSelector(selectSectionData(ANALYTICS_SECTION.BY_CATEGORY))
  const byCategoryFetching = useSelector(selectSectionFetching(ANALYTICS_SECTION.BY_CATEGORY))

  useEffect(() => {
    getBase(ANALYTICS_SECTION.WIN_RATE_MATRIX)
  }, [getBase])

  useEffect(() => {
    getCompare(ANALYTICS_SECTION.BY_CATEGORY)
  }, [getCompare])

  // The full page shows EVERY category (the top-N + resolved-only filter is the compact
  // card's rule on the Analytics page, not here), so the heat map rows line up one-to-one
  // with the table below; categories with nothing resolved render as a row of dash cells.
  const { cols, rows: matrixRows } = useMemo(() => toWinRateGrid(matrix), [matrix])

  // Per-category totals behind the heat map: the same categories in the same order.
  const tableRows = useMemo(
    () =>
      (byCategory?.result ?? []).map((row) => ({
        key: row.category,
        label: t(row.label),
        winRate: row.win_rate,
        won: row.won_count,
        resolved: row.resolved_count,
        count: row.count,
        value: row.amount
      })),
    [byCategory, t]
  )

  useEffect(() => {
    document.title = "Win rate by category and network - Chargeback"
    // This sub-page mounts inside the same Layout, so the shared page scroller
    // (#global-wrap) keeps the scroll position from the page we came from. Reset it
    // to the top (the app has no global scroll restoration on navigation).
    document.getElementById("global-wrap")?.scrollTo(0, 0)
  }, [])

  return (
    <div className={styles.page}>
      <Row gutter={[32, 32]}>
        <Col span={24}>
          <Button
            type="text"
            onClick={() => navigate(RoutesPaths.recover.analytics)}
            icon={<Icon type="chevron_left_small" />}
            style={{ marginLeft: -12 }}
            text={t("Back to Analytics")}
          />
        </Col>

        <Col span={24}>
          <Flex justify="space-between" align="center" gap={12} wrap="wrap">
            <Flex align="center" gap={8}>
              <Title level={4} style={{ marginBottom: 0 }}>
                {t("Win rate: category × network")}
              </Title>
              {/* Same caveat as the card this page opens from: a win rate that silently
                  included pre-connection decisions would read as ours. */}
              <InfoTooltip
                title={t(
                  "Win rate for each category and card scheme, over the disputes we fought to a decision. Cases decided before you connected are excluded."
                )}
                placement="top"
              />
            </Flex>
            <AnalyticsFilters />
          </Flex>
        </Col>

        <Col span={24}>
          <Spin spinning={matrixFetching}>
            {matrixRows.length ? (
              <div className={styles.winRateFull}>
                <WinRateGrid cols={cols} rows={matrixRows} />
              </div>
            ) : (
              <EmptyComponent type="no_data_available" style={{ minHeight: 450 }} />
            )}
          </Spin>
        </Col>

        <Col span={24}>
          <Table
            // The Chargeback Olympics look: the platform table chrome the component
            // already brings (`custom-table`), which the analytics `chart-table`
            // treatment used to strip. Columns unchanged.
            className={tables.olympics}
            dataSource={tableRows}
            columns={columns(t)}
            loading={byCategoryFetching}
            withOutPagination
            scroll={{}}
            rowKey="key"
            tableLayout="fixed"
            showHeader={!!tableRows.length}
            summary={() => (
              <SummaryRows
                summary={tableRows.length ? byCategory?.summary : null}
                comparisonSummary={byCategory?.comparison_summary}
                comparisonLabel={byCategory?.comparison_period?.label}
                cells={SUMMARY_CELLS}
              />
            )}
            locale={{ emptyText: <EmptyComponent type="no_data_available" /> }}
          />
        </Col>
      </Row>
    </div>
  )
}

export default WinRateFullPage
